import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppLayout, TopNavigation } from '@cloudscape-design/components';
import '@cloudscape-design/global-styles/index.css';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import awsExports from './aws-exports';

import TitleList from './pages/TitleList';
import TitleDetail from './pages/TitleDetail';
import CreateTitle from './pages/CreateTitle';
import EditTitle from './pages/EditTitle';
import Architecture from './pages/Architecture';
import LoginModal from './components/LoginModal';
import ChatBot from './components/ChatBot';
import FloatingChatButton from './components/FloatingChatButton';

Amplify.configure(awsExports);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showChatBot, setShowChatBot] = useState(false);
  const [username, setUsername] = useState('');

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const user = await getCurrentUser();
      setIsAuthenticated(true);
      // Use email if available, otherwise fallback to username
      const displayName = user.signInDetails?.loginId || user.username || 'User';
      setUsername(displayName);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleLogin = () => {
    checkAuthState();
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setIsAuthenticated(false);
      setUsername('');
    } catch (error) {
      console.log('Error signing out:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <TopNavigation
          identity={{
            href: "/",
            title: "Title ID Manager"
          }}
          utilities={[
            {
              type: "button",
              text: "Login",
              onClick: () => setShowLogin(true)
            }
          ]}
        />
        <AppLayout
          navigationHide
          content={
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h1>Welcome to Title ID Manager</h1>
              <p>Please login to access the application.</p>
            </div>
          }
        />
        <LoginModal
          visible={showLogin}
          onDismiss={() => setShowLogin(false)}
          onLogin={handleLogin}
        />
      </>
    );
  }

  return (
    <Router>
      <TopNavigation
        identity={{
          href: "/",
          title: "Title ID Manager"
        }}
        utilities={[
          {
            type: "button",
            text: "AI Assistant",
            iconName: "contact",
            onClick: () => setShowChatBot(true)
          },
          {
            type: "button",
            text: "Architecture",
            iconName: "status-info",
            href: "/architecture"
          },
          {
            type: "menu-dropdown",
            text: username,
            items: [
              {
                id: "logout",
                text: "Logout"
              }
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === 'logout') {
                handleLogout();
              }
            }
          }
        ]}
      />
      <AppLayout
        navigationHide
        content={
          <Routes>
            <Route path="/" element={<TitleList />} />
            <Route path="/titles/:id" element={<TitleDetail />} />
            <Route path="/create" element={<CreateTitle />} />
            <Route path="/edit/:id" element={<EditTitle />} />
            <Route path="/architecture" element={<Architecture />} />
          </Routes>
        }
      />
      <ChatBot
        visible={showChatBot}
        onDismiss={() => setShowChatBot(false)}
      />
      <FloatingChatButton onClick={() => setShowChatBot(true)} />
    </Router>
  );
}

export default App;